// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Raw Brokerage API Types
 *
 * Type definitions for all raw/base RTK Query endpoints in the brokerage domain.
 * These types follow the MSW pattern: Params, RequestBody, ResponseBody.
 *
 * @remarks
 * **Type Organization:**
 *
 * - **Base Types**: Reusable parameter types (BrokerConfig, ClientIdParam, etc.)
 * - **Schema Types**: Data structure types (Account, Transaction, Proposal, etc.)
 * - **Endpoint Types**: Request/Response types following MSW pattern
 *
 * **Type Naming Convention:**
 *
 * - `{EndpointName}Params`: Path and query parameters
 * - `{EndpointName}RequestBody`: Request body data
 * - `{EndpointName}ResponseBody`: Response data
 *
 * When the HTTP contract changes, regenerate the reference artifact with the
 * project’s `generate:brokerage-types` script, then align these curated types.
 * @example
 *
 * ```typescript
 * // Using endpoint types
 * const params: GetAccountParams = {
 *   broker_key: 'alpaca',
 *   type_account: 'PAPER',
 * };
 *
 * const body: GetAccountRequestBody = {
 *   account_id: 'DU1234567',
 * };
 *
 * // Using schema types
 * const account: Account = {
 *   buying_power: 50000,
 *   cash: 10000,
 *   currency: 'USD',
 *   equity: 60000,
 *   portfolio_value: 60000,
 * };
 * ```
 */

/** Broker integration keys accepted by the brokerage API. */
export enum BrokerKey {
  Alpaca = 'alpaca',
  InteractiveBrokers = 'interactive_brokers',
  Tradier = 'tradier',
}

/** Paper trading vs live account environment. */
export enum TypeAccount {
  Paper = 'PAPER',
  Live = 'LIVE',
}

/** Display / conversion currency for account and position values. */
export enum AccountCurrency {
  USD = 'USD',
  EUR = 'EUR',
}

/** Aggregator connection providers. */
export enum AggregatorProvider {
  Plaid = 'plaid',
  WealthReader = 'wealth_reader',
  Citec = 'citec',
  Psn = 'psn',
}

/** Institution lifecycle state from the aggregator API. */
export enum InstitutionStatus {
  Active = 'active',
  Pending = 'pending',
  Private = 'private',
}

/** Advisory proposal workflow state. */
export enum AdvisoryProposalStatus {
  Active = 'active',
  Pending = 'pending',
  Inactive = 'inactive',
}

/**
 * Broker configuration parameters
 *
 * Base type used in endpoints that require broker identification and account type.
 * This is one of the most commonly used parameter types across the brokerage API.
 *
 * @remarks
 * The `broker_key` identifies which broker platform to use, while `type_account`
 * determines whether to use paper trading (sandbox) or live trading environment.
 * @example
 *
 * ```typescript
 * const config: BrokerConfig = {
 *   broker_key: 'alpaca',
 *   type_account: 'PAPER',
 * };
 * ```
 */
export interface BrokerConfig {
  broker_key: BrokerKey;
  type_account: TypeAccount;
}

/**
 * Client ID parameter
 *
 * Base type used in endpoints that optionally filter data by a specific client ID.
 * When provided, the endpoint returns data scoped to that client; when omitted,
 * data for the authenticated user is returned.
 *
 * @example
 *
 * ```typescript
 * // With client_id
 * const params: ClientIdParam = {
 *   client_id: 123,
 * };
 *
 * // Without client_id (uses authenticated user)
 * const params: ClientIdParam = {};
 * ```
 */
export interface ClientIdParam {
  /**
   * User ID of the client for data retrieval.
   *
   * When provided, filters results to data associated with this specific client.
   * When omitted, returns data for the authenticated user.
   */
  client_id?: number;
}

/**
 * Institution ID parameter
 *
 * Base type used in endpoints that require identification of a specific financial
 * institution. Institutions represent connected bank accounts, brokerages, or other
 * financial service providers.
 *
 * @example
 *
 * ```typescript
 * const params: InstitutionIdParam = {
 *   institution_id: 'ins_1234567890',
 * };
 * ```
 */
export interface InstitutionIdParam {
  /**
   * ID of the financial institution.
   *
   * This is typically a string identifier like `'ins_1234567890'` that uniquely
   * identifies a connected financial institution (bank, brokerage, etc.).
   */
  institution_id: string;
}

/**
 * Date range parameters
 *
 * Base type used in endpoints that require filtering data by a date range. Dates
 * should be provided in ISO 8601 format (YYYY-MM-DD).
 *
 * @example
 *
 * ```typescript
 * const params: DateRangeParams = {
 *   start_date: '2024-01-01',
 *   end_date: '2024-12-31',
 * };
 * ```
 */
export interface DateRangeParams {
  /**
   * Start date for the date range.
   *
   * Format: ISO 8601 date string (YYYY-MM-DD)
   *
   * @example '2024-01-01'
   */
  start_date: string;
  /**
   * End date for the date range.
   *
   * Format: ISO 8601 date string (YYYY-MM-DD)
   *
   * @example '2024-12-31'
   */
  end_date: string;
}

/**
 * Refresh parameter
 *
 * Base type used in endpoints that support forcing a data refresh from the source
 * system. When `true`, bypasses cache and fetches fresh data.
 *
 * @example
 *
 * ```typescript
 * // Force refresh
 * const params: RefreshParam = {
 *   refresh: true,
 * };
 *
 * // Use cached data if available
 * const params: RefreshParam = {
 *   refresh: false, // or omit entirely
 * };
 * ```
 */
export interface RefreshParam {
  /**
   * Refresh query parameter.
   *
   * When `true`, forces the endpoint to fetch fresh data from the source system,
   * bypassing any cached responses. When `false` or omitted, uses cached data if
   * available.
   */
  refresh?: boolean;
}

/**
 * Currency parameter
 *
 * Base type used in endpoints that support currency conversion or currency-specific
 * value calculations. When provided, account values and calculations are returned
 * in the specified currency.
 *
 * @example
 *
 * ```typescript
 * // Request USD values
 * const params: CurrencyParam = {
 *   currency: 'USD',
 * };
 *
 * // Request EUR values
 * const params: CurrencyParam = {
 *   currency: 'EUR',
 * };
 * ```
 */
export interface CurrencyParam {
  /**
   * Currency for the account values.
   *
   * When specified, all monetary values in the response will be converted to and
   * expressed in this currency. If omitted, the default currency (typically USD) is
   * used.
   */
  currency?: AccountCurrency;
}

/**
 * {@link BrokerConfig} plus optional {@link CurrencyParam}; shared by account list,
 * account info, equity prices, and portfolio positions endpoints.
 */
export type BrokerConfigWithCurrencyParam = BrokerConfig & CurrencyParam;

/**
 * Account
 *
 * Represents basic account information from a broker, including financial metrics
 * such as buying power, cash balance, equity, and portfolio value.
 *
 * @remarks
 * This is a simplified account representation. For more detailed account
 * information including investment amounts, unrealized gains, and local currency
 * values, use {@link AccountInfo}.
 * @example
 *
 * ```typescript
 * const account: Account = {
 *   buying_power: 50000,
 *   cash: 10000,
 *   currency: 'USD',
 *   equity: 60000,
 *   portfolio_value: 60000,
 * };
 * ```
 */
export type Account = {
  /** Account buying power (available funds for trading) */
  buying_power: number;
  /** Account cash balance */
  cash: number;
  /** Account currency code (e.g., 'USD', 'EUR') */
  currency: string;
  /** Account equity (total account value) */
  equity: number;
  /** Account portfolio value (total value of holdings) */
  portfolio_value: number;
};

/**
 * BrokerageInfo
 *
 * Represents account identification information used in various endpoints to
 * specify which account to operate on.
 *
 * @remarks
 * This type is commonly used in request bodies to identify a specific account when
 * multiple accounts are available for a user. It's a required field in many
 * portfolio and account-related operations.
 * @example
 *
 * ```typescript
 * const brokerageInfo: BrokerageInfo = {
 *   account_id: 'DU1234567',
 * };
 * ```
 */
export type BrokerageInfo = {
  /**
   * Account identification.
   *
   * Unique identifier for a specific broker account. This is typically provided by
   * the broker platform (e.g., 'DU1234567' for Interactive Brokers).
   */
  account_id: string;
};

/**
 * BaseAccountValues
 *
 * Base account values containing core financial metrics for an account. This type
 * represents the fundamental financial data that is common across different account
 * representations.
 *
 * @remarks
 * This is a base type that is often extended or used as a component in more complex
 * account information types. All monetary values are in the specified currency.
 * @example
 *
 * ```typescript
 * const accountValues: BaseAccountValues = {
 *   investment: 50000,
 *   unrealized_capital_gains: 5000,
 *   buying_power: 55000,
 *   cash: 10000,
 *   aum: 60000,
 *   currency: 'USD',
 * };
 * ```
 */
export type BaseAccountValues = {
  /** Total investment for the account (amount invested in securities) */
  investment: number | null;
  /** Profit and Loss for the account (unrealized gains/losses) */
  unrealized_capital_gains: number | null;
  /** Total buying power for the account (available funds for trading) */
  buying_power: number | null;
  /** Total cash in the account (liquid funds) */
  cash: number | null;
  /** Assets under management for the account (total portfolio value) */
  aum: number | null;
  /** Currency for the account values ('USD' or 'EUR') */
  currency: AccountCurrency | null;
};

/**
 * AccountInfo
 *
 * Comprehensive account information including financial metrics, account
 * identification, and metadata. This is the most detailed account representation
 * available in the API.
 *
 * @remarks
 * This type extends {@link BaseAccountValues} with additional account metadata such
 * as account ID, type, name, manager, and local currency values. Use this type when
 * you need complete account information including currency conversions.
 * @example
 *
 * ```typescript
 * const accountInfo: AccountInfo = {
 *   account_id: 'DU1234567',
 *   account_type: 'PAPER',
 *   name: 'My Trading Account',
 *   investment: 50000,
 *   unrealized_capital_gains: 5000,
 *   buying_power: 55000,
 *   cash: 10000,
 *   aum: 60000,
 *   currency: 'USD',
 *   local_currency_values: {
 *     investment: 45000,
 *     currency: 'EUR',
 *     // ... other EUR values
 *   },
 * };
 * ```
 */
export type AccountInfo = {
  /** Total investment for the account (amount invested in securities) */
  investment: number | null;
  /** Profit and Loss for the account (unrealized gains/losses) */
  unrealized_capital_gains: number | null;
  /** Total buying power for the account (available funds for trading) */
  buying_power: number | null;
  /** Total cash in the account (liquid funds) */
  cash: number | null;
  /** Assets under management for the account (total portfolio value) */
  aum: number | null;
  /** Currency for the account values ('USD' or 'EUR') */
  currency: AccountCurrency | null;
  /** Account identification (unique broker account ID) */
  account_id?: string | null;
  /** Account type (e.g., 'PAPER', 'LIVE', 'IRA') */
  account_type?: string | null;
  /** Account name or display name */
  name?: string | null;
  /** Account manager name */
  account_manager?: string | null;
  /** Account values in local currency as a BaseAccountValues object */
  local_currency_values?: BaseAccountValues | null;
  /** Account last name */
  last_name?: string | null;
};

/** Path and query parameters for Account Get info from the broker user account. */
export type GetAccountParams = BrokerConfig;

/** Request body for Account Get info from the broker user account. */
export type GetAccountRequestBody = BrokerageInfo;

/** Response body for Account */
export type GetAccountResponseBody = Account;

/**
 * Path and query parameters for Account All Get all accounts available for a user
 * in a Broker API.
 */
export type GetAccountAllParams = BrokerConfigWithCurrencyParam;

/** Response body for Account All */
export type GetAccountAllResponseBody = AccountInfo[];

/**
 * BrokerCredentials
 *
 * Broker Credentials Base schema definition.
 */
export type BrokerCredentials = {
  /** Broker token */
  token?: string | null;
  /** Broker token secret */
  token_secret?: string | null;
  /** OAuth verifier */
  oauth_verifier?: string | null;
  /** Live session token */
  live_session_token?: string | null;
  /** Broker token expiration date */
  expiration_date?: number | null;
};

/**
 * Path and query parameters for Brokers Credentials Get Credentials for a specific
 * broker.
 */
export type GetAccountBrokersCredentialsParams = {
  broker_key: BrokerKey;
};

/** Response body for Brokers Credentials */
export type GetAccountBrokersCredentialsResponseBody = BrokerCredentials;

/**
 * BrokerConnectionStatus
 *
 * Broker Connection Status Base schema definition.
 */
export type BrokerConnectionStatus = {
  /** Broker connection status */
  connected?: boolean | null;
  /** Broker account type */
  type_account?: string | null;
  /** Broker token */
  token?: string | null;
};

/**
 * Path and query parameters for Brokers Credentials Save the Credentials for a
 * specific broker.
 */
export type CreateAccountBrokersCredentialsParams = {
  broker_key: BrokerKey;
};

/** Request body for Brokers Credentials Save the Credentials for a specific broker. */
export type CreateAccountBrokersCredentialsRequestBody = BrokerCredentials;

/** Response body for Brokers Credentials */
export type CreateAccountBrokersCredentialsResponseBody = BrokerConnectionStatus;

/**
 * Request body for Account Info Get account information of a user by account id in
 * a in a Broker API.
 */
export type GetAccountInfoRequestBody = BrokerageInfo;

/** Response body for Account Info */
export type GetAccountInfoResponseBody = AccountInfo;

/**
 * Transaction
 *
 * Transaction Base schema definition.
 */
export type Transaction = {
  /** Transaction date */
  date: string;
  /** Transaction amount */
  amount: number;
  /** Transaction currency */
  currency: string;
  /** Transaction type */
  type?: string | null;
  /** Transaction description */
  description?: string | null;
  /** Transaction ticker symbol */
  symbol?: string | null;
};

/**
 * Path and query parameters for Transactions Get transactions from the broker user
 * account.
 */
export type GetAccountTransactionParams = BrokerConfig;

/** Request body for Transactions Get transactions from the broker user account. */
export type GetAccountTransactionRequestBody = BrokerageInfo;

/** Response body for Transactions */
export type GetAccountTransactionResponseBody = Transaction[];

//#region ADVISORY SCHEMA TYPES

/**
 * AccountsByClient
 *
 * Accounts by client schema definition.
 */
export type AccountsByClient = {
  /** The IBANs of the accounts to share. */
  ibans?: string[];
  /** Client ID. */
  client_id?: number | null;
};

/**
 * AccountsWithDates
 *
 * Accounts with dates schema definition.
 */
export type AccountsWithDates = {
  /** Currency to convert the balances to. */
  currency: AccountCurrency;
  /** The IBANs of the accounts to share. */
  ibans?: string[];
  /** Client ID. */
  client_id?: number | null;
  /** Start date for the date range. */
  start_date: string;
  /** End date for the date range. */
  end_date: string;
};

/**
 * AccountHoldings
 *
 * Account holdings schema definition.
 */
export type AccountHoldings = {
  /** ID of the financial institution. */
  institution_id: string;
  /** Account IBAN. */
  iban: string;
  /** Datetime of the last positions snapshot */
  last_sync: string;
  /** Account holdings. */
  holdings: ClientHoldings[];
};

/**
 * ClientHoldings
 *
 * Client Holdings schema definition.
 */
export type ClientHoldings = {
  /** Position's ID */
  id?: number | null;
  /** The International Securities Identification Number. */
  isin?: string | null;
  /** The stock ticker transaction. */
  ticker?: string | null;
  /** The descriptive name of the transaction. */
  name: string;
  /** The price per unit at the time of the transaction. */
  price: number;
  /** The acquisition cost of the transaction. */
  acquisition_cost?: number | null;
  /** The number of units involved in the transaction. */
  quantity: number;
  /** The total investment value of the transaction. */
  investment: number;
  /** The currency in which the transaction was conducted. */
  currency: string;
  /** Type of the holding. */
  asset_type: string;
  /** USD rate of the holding. */
  usd_rate: number;
  /** EUR rate of the holding. */
  eur_rate: number;
};

/**
 * AccountTransactions
 *
 * Account transactions schema definition.
 */
export type AccountTransactions = {
  /** ID of the institution. */
  institution_id: string;
  /** IBAN of the account. */
  iban: string;
  /** List of transactions. */
  transactions: AdvisoryTransaction[];
};

/**
 * AdvisoryTransaction
 *
 * Transaction detail schema for client account operations.
 */
export type AdvisoryTransaction = {
  /** Unique transaction identifier */
  id?: string | null;
  /** Transaction description */
  description?: string | null;
  /** Transaction execution date */
  date: string;
  /** Instrument or asset name */
  name?: string | null;
  /** ISIN code (ISO 6166). Null for non-security transactions */
  isin?: string | null;
  /** Stock ticker symbol. Null for non-traded instruments */
  ticker?: string | null;
  /** Transaction amount. Positive for credits, negative for debits */
  amount?: number | null;
  /** Number of units. Null or zero for cash-only transactions */
  quantity?: number | null;
  /** Price per unit at execution. Null for non-security transactions */
  price?: number | null;
  /** Transaction type: buy, sell, fee */
  type?: string | null;
  /** Asset category: stock, etf, bond, pension-plan */
  asset_type?: string | null;
  /** Additional transaction classification */
  subtype?: string | null;
  /** ISO 4217 currency code */
  currency?: string | null;
  /** Record creation timestamp */
  created_at: string;
};

/**
 * TransactionsPayload
 *
 * Transactions payload schema.
 */
export type TransactionsPayload = {
  /** The IBANs of the accounts to share. */
  ibans?: string[];
  /** Client ID. */
  client_id?: number | null;
  /** Filter transactions from this date (inclusive) */
  start_date: string;
};

/**
 * Client
 *
 * Client schema definition. Aligned with OpenAPI list client (no nested
 * institutions).
 */
export type Client = {
  /** Client email address */
  email: string;
  /** Client's full name. Read-only. */
  name?: string | null;
  /** Client's first or given name */
  first_name?: string | null;
  /** Client's second or middle name */
  second_name?: string | null;
  /** Client's first (paternal) surname */
  first_surname?: string | null;
  /** Client's second (maternal) surname */
  second_surname?: string | null;
  /** Client ID */
  id: number;
  /** Citec user ID */
  citec_user_id: number;
  /** Client creation date */
  created_at: string;
  /** Status of the advisory proposal between the advisor and client. */
  status?: AdvisoryProposalStatus | null;
  /** Client's risk profile */
  risk_profile?: string | null;
  /** Total volume for the client in the list currency */
  total_volume: number;
};

/**
 * ClientsPage
 *
 * Paginated clients list response schema.
 */
export type ClientsPage = {
  data: Client[];
  pages: number;
  total_clients: number;
  active_clients: number;
  total_amount: number;
};

/**
 * CreateClient
 *
 * Create a new client.
 */
export type CreateClient = {
  /** Client email address */
  email: string;
  /** Client's first or given name */
  first_name?: string | null;
  /** Client's second or middle name */
  second_name?: string | null;
  /** Client's first (paternal) surname */
  first_surname?: string | null;
  /** Client's second (maternal) surname */
  second_surname?: string | null;
};

/**
 * UpdateClientProfile
 *
 * Update client profile.
 */
export type UpdateClientProfile = {
  /** Client's first or given name */
  first_name?: string | null;
  /** Client's second or middle name */
  second_name?: string | null;
  /** Client's first (paternal) surname */
  first_surname?: string | null;
  /** Client's second (maternal) surname */
  second_surname?: string | null;
  /** Client's date of birth in YYYY-MM-DD format */
  birth_date?: string | null;
  /** Client's contact phone number, including country code */
  phone_number?: string | null;
  /** Client's full street address, including house or apartment number */
  address?: string | null;
  /** City where the client resides */
  city?: string | null;
  /**
   * Client's tax identification number (NIF) or foreigner identification number
   * (NIE)
   */
  nif_nie?: string | null;
  /** Country where the client resides */
  country?: string | null;
  /** Postal or ZIP code of the client's address */
  postal_code?: string | null;
};

/**
 * CitecUserClient
 *
 * Citec user client schema definition.
 */
export type CitecUserClient = {
  /** Client's full name. Read-only. */
  name?: string | null;
  /** Client's first or given name. Never the full name. */
  first_name?: string | null;
  /** Client's second or middle name. */
  second_name?: string | null;
  /** Client's first (paternal) surname. */
  first_surname?: string | null;
  /** Client's second (maternal) surname. */
  second_surname?: string | null;
  /** Client's date of birth in YYYY-MM-DD format */
  birth_date?: string | null;
  /** Client's contact phone number, including country code */
  phone_number?: string | null;
  /** Client's full street address, including house or apartment number */
  address?: string | null;
  /** City where the client resides */
  city?: string | null;
  /**
   * Client's tax identification number (NIF) or foreigner identification number
   * (NIE)
   */
  nif_nie?: string | null;
  /** Country where the client resides */
  country?: string | null;
  /** Postal or ZIP code of the client's address */
  postal_code?: string | null;
  /** Client email address */
  email: string;
  /** Client ID */
  id: number;
  /** Citec user ID */
  citec_user_id: number;
  /** Citec user client creation date. */
  created_at: string;
  /** Advisory proposal for the client */
  advisory_proposal?: AdvisoryProposal | null;
};

/**
 * ClientProfile
 *
 * Client profile schema definition.
 */
export type ClientProfile = {
  /** Client's full name. Read-only. */
  name?: string | null;
  /** Client's first or given name. Never the full name. */
  first_name?: string | null;
  /** Client's second or middle name. */
  second_name?: string | null;
  /** Client's first (paternal) surname. */
  first_surname?: string | null;
  /** Client's second (maternal) surname. */
  second_surname?: string | null;
  /** Client's document number (canonical). */
  document_number?: string | null;
  /** Residential street name (canonical). */
  street_name?: string | null;
  /** Client's date of birth in YYYY-MM-DD format */
  birth_date?: string | null;
  /** Client's contact phone number, including country code */
  phone_number?: string | null;
  /** Client's full street address, including house or apartment number */
  address?: string | null;
  /** City where the client resides */
  city?: string | null;
  /**
   * Client's tax identification number (NIF) or foreigner identification number
   * (NIE)
   */
  nif_nie?: string | null;
  /** Country where the client resides */
  country?: string | null;
  /** Postal or ZIP code of the client's address */
  postal_code?: string | null;
  /** Client email address */
  email: string;
  /** Client ID */
  id: number;
  /** Citec user ID */
  citec_user_id: number;
  /** Client creation date */
  created_at: string;
  /** List of institutions */
  institutions?: (Institution | AdvisoryInstitution)[];
  /** Client's risk profile */
  risk_profile?: string | null;
  /** Advisory proposal for the client */
  advisory_proposal?: AdvisoryProposal | null;
};

/**
 * Institution
 *
 * Institution data. Aligned with OpenAPI Institution schema.
 */
export type Institution = {
  id: string;
  name: string;
  /** Status of shared token */
  status?: InstitutionStatus | null;
  /** Aggregator provider */
  provider: AggregatorProvider;
  logo_url?: string | null;
};

/**
 * InstitutionProductOwner
 *
 * Owner information for institution products.
 */
export type InstitutionProductOwner = {
  /** Full name of the product owner */
  name?: string | null;
  /** Ownership role or authority level */
  role?: string | null;
};

export enum AccountStatus {
  Active = 'active',
  InactiveDocsSent = 'inactive_docs_sent',
  InactiveDocsPending = 'inactive_docs_pending',
  Cancelled = 'cancelled',
  CancellationPending = 'cancellation_pending',
  Pending = 'pending',
}

/**
 * InstitutionAccount
 *
 * Account product schema for advisory institutions.
 */
export type InstitutionAccount = {
  /** Display name of the product */
  name?: string | null;
  /** Product type classification */
  type?: string | null;
  /** Account identifier or reference number */
  iban: string;
  /** ISO 4217 currency code */
  currency?: string | null;
  /** List of individuals or entities with ownership rights */
  owners?: InstitutionProductOwner[];
  /** Available cash balance in the account */
  cash?: number | null;
  /** Outstanding debt or negative balance */
  debt?: number | null;
  /** Available liquidity in the account (used for KPIs and drawer). */
  available?: number | null;
  /** Current sharing status of the account with advisors */
  status?: string | null;
  /** Timestamp when the account sharing relationship became active */
  valid_from?: string | null;
  /** Investment value of the account */
  investment?: number | null;
  /** Current operational status of the account */
  account_status?: AccountStatus | null;
};

/**
 * InstitutionCard
 *
 * Card product schema for advisory institutions.
 */
export type InstitutionCard = {
  /** Display name of the product */
  name?: string | null;
  /** Product type classification */
  type?: string | null;
  /** Account identifier or reference number */
  iban: string;
  /** ISO 4217 currency code */
  currency?: string | null;
  /** List of individuals or entities with ownership rights */
  owners?: InstitutionProductOwner[];
  /** Annual percentage rate (APR) for credit cards */
  anual_interest_rate?: number | null;
  /** Maximum credit limit or spending limit for the card */
  limit: number;
  /** Current amount used or disposed from the available credit */
  disposed?: number | null;
};

/**
 * InstitutionLoan
 *
 * Loan product schema for advisory institutions.
 */
export type InstitutionLoan = {
  /** Display name of the product */
  name?: string | null;
  /** Product type classification */
  type?: string | null;
  /** Account identifier or reference number */
  iban: string;
  /** ISO 4217 currency code */
  currency?: string | null;
  /** List of individuals or entities with ownership rights */
  owners?: InstitutionProductOwner[];
  /** Date when the loan agreement was originated or constituted */
  constitution_date?: string | null;
  /** Annual interest rate applied to the loan as a decimal */
  interest_rate?: number | null;
  /** Original principal amount granted at loan origination */
  granted_capital?: number | null;
  /** Total principal amount repaid to date */
  total_amortized?: number | null;
  /** Total interest paid over the life of the loan to date */
  total_interest?: number | null;
  /** Amount due for the next scheduled payment */
  next_installment?: number | null;
  /** Remaining principal balance to be repaid */
  pending_amount?: number | null;
  /** Number of remaining payment installments */
  pending_receipts?: number | null;
};

/**
 * InstitutionPortfolio
 *
 * Portfolio product schema for advisory institutions.
 */
export type InstitutionPortfolio = {
  /** Display name of the product */
  name?: string | null;
  /** Product type classification */
  type?: string | null;
  /** Account identifier or reference number */
  iban: string;
  /** ISO 4217 currency code */
  currency?: string | null;
  /** List of individuals or entities with ownership rights */
  owners?: InstitutionProductOwner[];
  /** Current total market value of all investments in the portfolio */
  investment?: number | null;
};

/**
 * InstitutionProducts
 *
 * Collection of all financial products held at an institution.
 */
export type InstitutionProducts = {
  /** List of account products */
  accounts: InstitutionAccount[];
  /** List of card products */
  cards: InstitutionCard[];
  /** List of loan products */
  loans: InstitutionLoan[];
  /** List of portfolio products */
  portfolios: InstitutionPortfolio[];
};

/**
 * AdvisoryInstitution
 *
 * Advisory institution information for client financial data. Aligned with OpenAPI
 * AdvisoryInstitution schema.
 */
export type AdvisoryInstitution = {
  id: string;
  institution_id: string;
  name: string;
  provider: AggregatorProvider;
  logo_url?: string | null;
  last_sync?: string | null;
  products: InstitutionProducts;
  error?: string | null;
  is_oneshot?: boolean;
  is_business?: boolean;
};

/**
 * AdvisoryInstitutionSearchItem
 *
 * Advisory institution search item from providers catalog.
 */
export type AdvisoryInstitutionSearchItem = {
  /** Institution identifier from provider catalog. */
  id: string;
  /** Display name of the institution. */
  name: string;
  /** Logo URL. */
  logo_url: string;
  /** Country code (ISO 3166-1 alpha-2). */
  country: string;
  /** Whether the institution connection is one-shot. */
  is_oneshot: boolean;
  /** Whether the institution is business-oriented. */
  is_business: boolean;
  /** Provider identifier. */
  provider: AggregatorProvider | string;
};

export enum AdvisoryBankProductType {
  Account = 'account',
  Card = 'card',
  Loan = 'loan',
  Portfolio = 'portfolio',
}

export type AdvisoryBankAccount = InstitutionAccount & {
  product_type: AdvisoryBankProductType.Account;
  balance: number;
};

export type AdvisoryBankCard = InstitutionCard & {
  product_type: AdvisoryBankProductType.Card;
  balance: number;
};

export type AdvisoryBankLoan = InstitutionLoan & {
  product_type: AdvisoryBankProductType.Loan;
  balance: number;
};

export type AdvisoryBankPortfolio = InstitutionPortfolio & {
  product_type: AdvisoryBankProductType.Portfolio;
  balance: number;
};

export type AdvisoryBankProduct =
  | AdvisoryBankAccount
  | AdvisoryBankCard
  | AdvisoryBankLoan
  | AdvisoryBankPortfolio;

/** Recoverable bank-link error classification for aggregator UI. */
export enum AdvisoryBankError {
  Reconnect = 'reconnect',
  Retry = 'retry',
  Ignore = 'ignore',
}

export interface AdvisoryBank
  extends Omit<AdvisoryInstitution, 'products' | 'error' | 'errorMessage'> {
  products: AdvisoryBankProduct[];
  balance: number;
  error?: AdvisoryBankError;
  errorMessage?: string | null;
}

/**
 * InstitutionCredentials
 *
 * Institution credentials schema definition.
 */
export type InstitutionCredentials = {
  public_token: string;
  code_verifier?: string | null;
  provider: AggregatorProvider;
  redirect_uri?: string | null;
};

/**
 * LinkTokenResponse
 *
 * Plaid Link Create Response schema definition.
 */
export type LinkTokenResponse = {
  /** Plaid Link token. */
  link_token: string;
};

/**
 * SyncInstitutions
 *
 * Sync institutions schema. Aligned with OpenAPI (connection_ids are numeric).
 */
export type SyncInstitutions = {
  /** The IDs of the connections to sync. */
  connection_ids: (number | string)[];
  /** The ID of the client to sync. */
  client_id?: number | null;
  /** Start date for the date range. */
  start_date?: string | null;
};

/**
 * SyncResponse
 *
 * Response of POST /v2/advisory/institutions/sync/ (Sync Institutions).
 */
export type SyncResponse = {
  /** Sync batch ID; use with GET …/sync/{batch_id}/status/ to poll. */
  batch_id: string;
  /** Institution IDs included in the sync. */
  institutions: string[];
};

/**
 * InstitutionSyncStatus
 *
 * Institution sync status schema (per connection).
 */
export type InstitutionSyncStatus = {
  /** ID of the financial institution. */
  institution_id: string;
  /** Connection ID. */
  connection_id: number;
  /** Status. */
  status: string;
  /** Message. */
  message?: string | null;
  /** Error code. */
  error_code?: string | null;
  /** Last updated at. */
  last_updated_at?: string | null;
};

/**
 * SyncStatus
 *
 * Sync status schema for a batch (GET
 * /v2/advisory/institutions/sync/{batch_id}/status/).
 */
export type SyncStatus = {
  /** Batch ID. */
  batch_id: string;
  /** Started at. */
  started_at?: string | null;
  /** Whether the sync batch is completed. */
  is_completed: boolean;
  /** Per-connection sync status. */
  connections?: Record<string, InstitutionSyncStatus>;
};

/**
 * AdvisoryProposal
 *
 * Advisory proposal schema definition.
 */
export type AdvisoryProposal = {
  /** Advisory proposal ID */
  id: number;
  /** Type of advisory */
  advisory_type: string;
  /** Commission managed assets */
  commission_managed_assets: number;
  /** Proposal status */
  status: AdvisoryProposalStatus;
  /** List of institutions (with products.accounts) included in the proposal */
  institutions: AdvisoryInstitution[];
  /** Creation timestamp */
  created_at: string;
  /** Update timestamp */
  updated_at: string;
  /** Contract associated with the Advisory Proposal */
  contract?: unknown | null;
  /** Information about the advisor */
  advisor?: { name?: string | null } | null;
  /** Advisor Citec user id that owns this proposal. */
  user_id: number;
};

/**
 * CreateAdvisoryProposal
 *
 * Create an advisory proposal.
 */
export type CreateAdvisoryProposal = {
  /** Client ID */
  client_id: number;
  /** Account IBANs to include in the proposal */
  ibans: string[];
  /** Type of advisory */
  advisory_type: string;
  /** Commission managed assets */
  commission_managed_assets: number;
};

/**
 * UpdateAdvisoryProposal
 *
 * Update an advisory proposal.
 */
export type UpdateAdvisoryProposal = {
  /** Confirm or reject the proposal */
  confirm: boolean;
};

/**
 * CreateAdvisoryContract
 *
 * Defines the necessary data to create a new advisory contract.
 */
export type CreateAdvisoryContract = {
  /**
   * The unique ID of the associated advisory proposal from which this contract
   * originates.
   */
  advisory_proposal_id: number;
  /**
   * A dictionary of onboarding questions presented to the client and their
   * corresponding answers, keyed by question ID.
   */
  onboarding_qa: Record<string, unknown>;
  /**
   * Flag to activate MiFID II test results calculation. When True, suitability and
   * appropriateness scores will be calculated
   */
  calculate_test_results: boolean;
};

/**
 * MiFIDTestResults
 *
 * MiFID II test results response.
 */
export type MiFIDTestResults = {
  /** Suitability test results. */
  suitability?: { profile?: string | null; score?: number | null } | null;
  /** Appropriateness test results. */
  appropriateness?: { instruments?: string[] | null; score?: number | null } | null;
  /** Test results expiration date (today + 1 year). */
  expiration_date?: string | null;
};

/**
 * Advisor
 *
 * Advisor data.
 */
export type Advisor = {
  /** Advisor ID */
  id: number;
  /** Advisor email address */
  email: string;
  /** Advisor's full name. Read-only. */
  name?: string | null;
  /** Advisor phone number from Citec user profile. */
  phone_number?: string | null;
  /** List of linked institutions */
  institutions: AdvisoryInstitution[];
};

/**
 * ShareAccounts
 *
 * Share accounts with users schema.
 */
export type ShareAccounts = {
  /** The IBANs of the accounts to share. */
  ibans?: string[] | null;
  /** The email of the user to share accounts with. */
  user_email: string;
};

/**
 * UnshareAccounts
 *
 * Unshare accounts schema.
 */
export type UnshareAccounts = {
  /** The IBANs of the accounts to unshare. */
  ibans: string[];
  /** The ID of the user to unshare accounts from. */
  user_id: number;
};

/**
 * UpdateSharedAccountStatus
 *
 * Update shared account status schema.
 */
export type UpdateSharedAccountStatus = {
  /** The IBANs of the accounts to share. */
  ibans?: string[] | null;
  /** Confirm the shared account status. */
  confirm: boolean;
};

/**
 * DebtAmortization
 *
 * Single row in a loan or credit line amortization schedule.
 */
export type DebtAmortization = {
  /** Scheduled payment date; null when not confirmed by the institution */
  installment_date: string | null;
  /** Payment status (e.g. `paid`, `pending`) */
  status: string;
  /** Capital repaid in this installment */
  amortized: number | null;
  /** Interest charged in this installment */
  interest: number | null;
  /** Total installment amount (capital + interest) */
  total: number | null;
  /** Outstanding principal after this installment */
  pending_capital: number | null;
};

/**
 * AdvisoryDebtCreditLine
 *
 * Credit-line or credit-card product with its amortization schedule.
 */
export type AdvisoryDebtCreditLine = {
  /** Unique identifier assigned by the financial institution */
  provider_uuid: string;
  /** Product subtype (e.g. `credit_line`, `credit_card`) */
  product_type: string;
  /** Commercial name of the product */
  name: string | null;
  /** Original currency as reported by the provider */
  currency: string;
  /** Contract or reference number at the institution */
  contract_number?: string | null;
  /** Total approved credit limit */
  limit_amount?: number | null;
  /** Annual nominal interest rate */
  interest_rate?: number | null;
  /** Date the product was opened */
  constitution_date?: string | null;
  /** Scheduled expiration date */
  expiration_date?: string | null;
  /** Remaining available credit */
  available?: number | null;
  /** Amount currently drawn */
  disposed?: number | null;
  /** Current net balance; negative values indicate debt */
  current_balance?: number | null;
  /** Date of the next scheduled payment */
  next_payment_date?: string | null;
  /** Amortization schedule */
  amortizations: DebtAmortization[];
};

/**
 * AdvisoryDebtLoan
 *
 * Loan product with its amortization schedule.
 */
export type AdvisoryDebtLoan = {
  /** Unique identifier assigned by the financial institution */
  provider_uuid: string;
  /** Commercial name of the loan product */
  name: string | null;
  /** Original currency of the loan as reported by the provider */
  currency: string;
  /** Contract or reference number at the institution */
  contract_number?: string | null;
  /** Total capital originally granted */
  granted_capital?: number | null;
  /** Annual nominal interest rate */
  interest_rate?: number | null;
  /** Date the loan was originated */
  constitution_date?: string | null;
  /** Scheduled maturity date */
  expiration_date?: string | null;
  /** Outstanding balance */
  pending_amount?: number | null;
  /** Amount of the next scheduled payment */
  next_installment?: number | null;
  /** Number of remaining installments */
  pending_receipts?: number | null;
  /** Amortization schedule */
  amortizations: DebtAmortization[];
};

/**
 * AccountHistoricalDebt
 *
 * Daily time-series arrays classifying outstanding debt by maturity. Each index
 * corresponds to the same position in the parent `dates` array. Granularity follows
 * the requested period (daily, weekly, or monthly buckets).
 *
 * @remarks
 * Short_term: debt expiring within 12 months from each snapshot date. long_term:
 * debt expiring beyond 12 months from each snapshot date.
 */
export type AccountHistoricalDebt = {
  /** Daily short-term debt values (expiration ≤ 12 months) */
  short_term: number[];
  /** Daily long-term debt values (expiration > 12 months) */
  long_term: number[];
};

/**
 * AdvisoryDebtAccount
 *
 * Debt data for a single account: credit lines, loans, and historical debt
 * evolution keyed to the top-level shared `dates` axis.
 */
export type AdvisoryDebtAccount = {
  /** ID of the financial institution */
  institution_id: string;
  /** Account IBAN */
  iban?: string | null;
  /** Timestamp of the last successful sync */
  last_sync: string;
  /** Credit-line products with amortization schedules */
  credit_lines: AdvisoryDebtCreditLine[];
  /** Loan products with amortization schedules */
  loans: AdvisoryDebtLoan[];
  /** Daily historical debt split by maturity (aligned to top-level dates) */
  historical_debt: AccountHistoricalDebt;
};

/**
 * AdvisoryAccountsDebt
 *
 * Top-level response for POST /v2/advisory/accounts/debts/.
 *
 * @remarks
 * `dates` is the single shared time axis for all `accounts[].historical_debt`
 * series — it is not duplicated per account.
 */
export type AdvisoryAccountsDebt = {
  /** ISO 4217 currency for all monetary values */
  currency: string;
  /** Shared date axis (ISO 8601 strings); granularity matches the period window */
  dates: string[];
  /** Per-account debt data */
  accounts: AdvisoryDebtAccount[];
};

//#endregion ADVISORY SCHEMA TYPES

//#region ADVISORY ENDPOINT TYPES

/**
 * Path and query parameters for Investment Positions Returns the investment
 * positions for a group of accounts.
 */
export type GetAdvisoryAccountsPositionsParams = {
  /** Add positions query parameter */
  add_positions?: boolean;
};

/** Request body for Investment Positions */
export type GetAdvisoryAccountsPositionsRequestBody = AccountsByClient;

/** Response body for Investment Positions */
export type GetAdvisoryAccountsPositionsResponseBody = AccountHoldings[];

/**
 * Path and query parameters for Account Transactions Get account transactions for a
 * group of provided accounts.
 */
export type GetAdvisoryAccountsTransactionsParams = {
  /** Add positions query parameter */
  add_positions?: boolean;
};

/** Request body for Account Transactions */
export type GetAdvisoryAccountsTransactionsRequestBody = TransactionsPayload;

/** Response body for Account Transactions */
export type GetAdvisoryAccountsTransactionsResponseBody = AccountTransactions[];

/** Request body for Historical Balances */
export type GetAdvisoryAccountsBalancesHistoricalRequestBody = AccountsWithDates;

/** Response body for Historical Balances */
export type GetAdvisoryAccountsBalancesHistoricalResponseBody =
  AccountHistoricalBalances[];

/** Path and query parameters for List Clients */
export type GetAdvisoryClientsParams = {
  search?: string;
  currency?: AccountCurrency;
  page?: number;
  page_size?: number;
};

/** Response body for List Clients */
export type GetAdvisoryClientsResponseBody = ClientsPage;

/** Request body for Update Client */
export type UpdateAdvisoryClientsRequestBody = UpdateClientProfile;

/** Response body for Update Client */
export type UpdateAdvisoryClientsResponseBody = void;

/** Request body for Create Client */
export type CreateAdvisoryClientsRequestBody = CreateClient;

/** Response body for Create Client */
export type CreateAdvisoryClientsResponseBody = Client;

/**
 * Path and query parameters for Retail Client Access Verify if the client was
 * invited by a citec user.
 */
export type GetAdvisoryClientsAccessParams = {
  /** Email address of the client to search */
  client_email: string;
};

/** Response body for Retail Client Access */
export type GetAdvisoryClientsAccessResponseBody = unknown;

/** Response body for Get Citec User Client Profile */
export type GetAdvisoryClientsProfileResponseBody = CitecUserClient;

/**
 * Path and query parameters for Delete Citec user client Deletes the specified
 * client.
 */
export type DeleteAdvisoryClientsClientIdParams = ClientIdParam;

/** Response body for Delete Citec user client */
export type DeleteAdvisoryClientsClientIdResponseBody = void;

/**
 * Path and query parameters for Get Client Profile Get the profile of a specific
 * client.
 */
export type GetAdvisoryClientsClientIdProfileParams = ClientIdParam;

/** Response body for Get Client Profile */
export type GetAdvisoryClientsClientIdProfileResponseBody = ClientProfile;

/**
 * Path and query parameters for List Institutions Get all available linked
 * institutions with their accounts.
 */
export type GetAdvisoryInstitutionsParams = ClientIdParam;

/** Response body for List Institutions */
export type GetAdvisoryInstitutionsResponseBody = AdvisoryInstitution[];

/**
 * Path and query parameters for Search Advisory Institutions List all available
 * bank institutions to connect.
 */
export type SearchAdvisoryInstitutionsParams = {
  /** Search query */
  q?: string;
  /** Country code */
  country?: string;
};

/** Response body for Search Advisory Institutions */
export type SearchAdvisoryInstitutionsResponseBody =
  AdvisoryInstitutionSearchItem[];

/** Request body for Create Institution */
export type CreateAdvisoryInstitutionsRequestBody = InstitutionCredentials;

/** Response body for Create Institution */
export type CreateAdvisoryInstitutionsResponseBody = AdvisoryInstitution;

/**
 * Path and query parameters for Create Link Token Create a link token to be used in
 * the Plaid Link flow.
 */
export type GetAdvisoryInstitutionsLinkParams = {
  /** ID of the financial institution */
  institution_id?: string | null;
};

/** Response body for Create Link Token */
export type GetAdvisoryInstitutionsLinkResponseBody = LinkTokenResponse;

/**
 * Path and query parameters for Sync Institutions Sync the institution financial
 * data from providers.
 */
export type CreateAdvisoryInstitutionsSyncParams = ClientIdParam;

/** Request body for Sync Institutions */
export type CreateAdvisoryInstitutionsSyncRequestBody = SyncInstitutions;

/** Response body for Sync Institutions */
export type CreateAdvisoryInstitutionsSyncResponseBody = SyncResponse;

/**
 * Path parameters for Get Sync Status (GET
 * /v2/advisory/institutions/sync/{batch_id}/status/).
 */
export type GetAdvisoryInstitutionsSyncBatchIdStatusParams = {
  /** Sync batch ID returned by POST /v2/advisory/institutions/sync/. */
  batch_id: string;
};

/** Response body for Get Sync Status */
export type GetAdvisoryInstitutionsSyncBatchIdStatusResponseBody = SyncStatus;

/**
 * Path and query parameters for Delete an Institution Delete a linked institution
 * indicated by its ID.
 */
export type DeleteAdvisoryInstitutionsInstitutionIdParams = InstitutionIdParam;

/** Response body for Delete an Institution */
export type DeleteAdvisoryInstitutionsInstitutionIdResponseBody = void;

/** Response body for Get Advisory Proposals */
export type GetAdvisoryProposalsResponseBody = AdvisoryProposal[];

/** Request body for Simulate Target Portfolio */
export type CreateAdvisoryProposalsTargetPortfolioRequestBody =
  TargetPortfolioRequest;

/** Response body for Simulate Target Portfolio */
export type CreateAdvisoryProposalsTargetPortfolioResponseBody =
  TargetPortfolioResponse;

/** Request body for Create Advisory Proposal */
export type CreateAdvisoryProposalsRequestBody = CreateAdvisoryProposal;

/** Response body for Create Advisory Proposal */
export type CreateAdvisoryProposalsResponseBody = void;

/** Request body for Update Advisory Contract */
export type CreateAdvisoryProposalsContractsRequestBody = CreateAdvisoryContract;

/** Response body for Update Advisory Contract */
export type CreateAdvisoryProposalsContractsResponseBody = MiFIDTestResults;

/**
 * Path and query parameters for Delete Advisory Proposal Delete an advisory
 * proposal.
 */
export type DeleteAdvisoryProposalsProposalIdParams = {
  /** ID of the advisory proposal */
  proposal_id: number;
};

/** Response body for Delete Advisory Proposal */
export type DeleteAdvisoryProposalsProposalIdResponseBody = void;

/**
 * Path and query parameters for Update Advisory Proposal Update the status of an
 * advisory proposal.
 */
export type UpdateAdvisoryProposalsProposalIdParams = {
  /** ID of the advisory proposal */
  proposal_id: number;
};

/** Request body for Update Advisory Proposal */
export type UpdateAdvisoryProposalsProposalIdRequestBody = UpdateAdvisoryProposal;

/** Response body for Update Advisory Proposal */
export type UpdateAdvisoryProposalsProposalIdResponseBody = void;

/** Path and query parameters for Sign Advisory Proposal Sign an advisory proposal. */
export type CreateAdvisoryProposalsProposalIdSignParams = {
  /** ID of the advisory proposal */
  proposal_id: number;
};

/** Response body for Sign Advisory Proposal */
export type CreateAdvisoryProposalsProposalIdSignResponseBody = void;

/** Response body for Get advisors with shared institutions */
export type GetAdvisoryUsersResponseBody = Advisor[];

/** Path and query parameters for Share accounts Share accounts with users. */
export type CreateAdvisoryUsersAccountsParams = void;

/** Request body for Share accounts */
export type CreateAdvisoryUsersAccountsRequestBody = ShareAccounts;

/** Response body for Share accounts */
export type CreateAdvisoryUsersAccountsResponseBody = void;

/** Path and query parameters for Update accounts Confirm or reject shared accounts. */
export type UpdateAdvisoryUsersAccountsParams = void;

/** Request body for Update accounts */
export type UpdateAdvisoryUsersAccountsRequestBody = UpdateSharedAccountStatus;

/** Response body for Update accounts */
export type UpdateAdvisoryUsersAccountsResponseBody = void;

/** Path and query parameters for Unshare accounts Unshare shared accounts. */
export type UnshareAdvisoryUsersAccountsParams = ClientIdParam;

/** Request body for Unshare accounts */
export type UnshareAdvisoryUsersAccountsRequestBody = UnshareAccounts;

/** Response body for Unshare accounts */
export type UnshareAdvisoryUsersAccountsResponseBody = void;

/**
 * Request body for Get Accounts Debt.
 *
 * Retrieves debt data (credit lines and loans) with daily historical evolution for
 * the accounts accessible to the authenticated user.
 *
 * **Base endpoint:** `POST /v2/advisory/accounts/debts/`
 */
export type GetAdvisoryAccountsDebtRequestBody = {
  /** Start of the time window (ISO 8601 date, inclusive) */
  start_date: string;
  /** End of the time window (ISO 8601 date, inclusive; defaults to today) */
  end_date?: string;
  /** Target currency for monetary values (converted server-side) */
  currency: AccountCurrency;
  /** IBANs to filter; when omitted returns all accessible accounts */
  ibans?: string[];
  /** Client ID; when provided restricts results to that client */
  client_id?: number | null;
};

/** Response body for Get Accounts Debt */
export type GetAdvisoryAccountsDebtResponseBody = AdvisoryAccountsDebt;

//#endregion ADVISORY ENDPOINT TYPES

//#region AGGREGATOR SCHEMA TYPES

/**
 * HistoricalBalances
 *
 * Historical balances schema definition.
 */
export type HistoricalBalances = {
  /** Cash */
  cash: number[];
  /** Debt */
  debt: number[];
  /** Investment */
  investment: number[];
  /** Net Value */
  net_value: number[];
  /** Dates */
  dates: string[];
};

/**
 * AccountHistoricalBalances
 *
 * Account historical balances schema definition.
 */
export type AccountHistoricalBalances = {
  /** ID of the financial institution. */
  institution_id: string;
  /** Account IBAN. */
  iban: string;
  /** Datetime of the last synchronization */
  last_sync: string;
  /** Historical balances data */
  historical_balances: HistoricalBalances;
};

/**
 * Cashflow
 *
 * Cashflow schema definition.
 */
export type Cashflow = {
  /** List of dates. */
  dates: string[];
  /** List of deposits. */
  deposits: number[];
  /** List of dispositions. */
  dispositions: number[];
  /** List of dividends. */
  dividends: number[];
  /** List of interests. */
  interests: number[];
  /** List of fees. */
  fees: number[];
};

/**
 * InProgressInfo
 *
 * In progress info schema definition.
 */
export type InProgressInfo = {
  /** Message */
  message: string;
  /** Callback url to retrieve the data from */
  callback_url?: string | null;
  /** Status of the task */
  status: string;
};

/**
 * InProgressResponse
 *
 * AggPositionsResponse schema definition.
 */
export type InProgressResponse = {
  /** Data */
  data: unknown[];
  /** In progress information */
  in_progress: InProgressInfo | null;
};

/**
 * User
 *
 * User schema definition.
 */
export type User = {
  /** Unique identifier for the user */
  id: number;
  /** Full name of the user */
  name?: string | null;
};

/**
 * Allocation
 *
 * Allocation schema definition.
 */
export type Allocation = {
  /** Category name */
  label: string;
  /** Weight of the category */
  weight: number;
  /** Amount of the category */
  amount: number;
};

/**
 * Exposure
 *
 * Exposure schema definition.
 */
export type Exposure = {
  /** Category name */
  label: string;
  /** Weight of the category */
  weight: number;
};

/**
 * Holding
 *
 * Holding schema definition.
 */
export type Holding = {
  /** Type of asset */
  asset_type?: string | null;
  /** Stock symbol or trading identifier */
  ticker?: string | null;
  /** International Securities Identification Number */
  isin?: string | null;
  /** Name of the holding */
  name?: string | null;
  /** Currency of the holding */
  currency?: string | null;
  /** Weight of the holding */
  weight?: number | null;
  /** Amount of the holding */
  amount?: number | null;
  /** Price per share or unit */
  price?: number | null;
  /** M3 return of the holding */
  m3_return?: number | null;
  /** M6 return of the holding */
  m6_return?: number | null;
  /** Y1 return of the holding */
  y1_return?: number | null;
};

/**
 * Portfolio
 *
 * Portfolio schema definition.
 */
export type Portfolio = {
  /** Returns of the portfolio */
  returns_3m?: number | null;
  /** Returns of the portfolio */
  returns_6m?: number | null;
  /** Returns of the portfolio */
  returns_1y?: number | null;
  /** Returns of the portfolio */
  returns_3y?: number | null;
  /** Returns of the portfolio */
  returns_ytd?: number | null;
  /** Volatility of the portfolio */
  volatility?: number | null;
  /** Max drawdown of the portfolio */
  max_drawdown?: number | null;
  /** List of holdings in the portfolio */
  positions: Holding[];
  /** Asset allocation exposure of the portfolio */
  markets: Allocation[];
  /** Currency exposure of the portfolio */
  currencies: Exposure[];
  /** Country exposure of the portfolio */
  countries: Exposure[];
  /** Segment exposure of the portfolio */
  segments: Exposure[];
};

/**
 * TsSignal
 *
 * Time series signal schema definition.
 */
export type TsSignal = {
  /** List of dates. Format 'DD-MM-YYYY' */
  date: string[];
  /** (Optional) List of benchmark signal */
  benchmark?: number[] | null;
  /** List of portfolio signal */
  portfolio: number[];
};

/**
 * RiskReturn
 *
 * Risk return schema definition.
 */
export type RiskReturn = {
  /** Name of the risk return */
  name: string;
  /** Volatility of the portfolio */
  volatility: number;
  /** Returns of the portfolio */
  returns: number;
};

/**
 * ProposalOperation
 *
 * Proposal operation schema definition.
 */
export type ProposalOperation = {
  /**
   * Whether the operation is entered by shares or amount
   *
   * @default shares
   */
  input_type?: 'shares' | 'amount';
  /** Type of trading operation (buy/sell/transfer) */
  operation_type: 'buy' | 'sell' | 'transfer';
  /**
   * Unique identifier for the origin financial account. Real IBAN of the selected
   * origin account.
   */
  from_iban: string;
  /**
   * Stock symbol or trading identifier of the origin. Set to asset currency for
   * 'buy' operation.
   */
  from_ticker?: string | null;
  /**
   * International Securities Identification Number of the origin. Set to asset
   * currency for 'buy' operation
   */
  from_isin?: string | null;
  /**
   * Unique identifier for the target financial account. Real IBAN of the selected
   * destination account.
   */
  to_iban: string;
  /**
   * Stock symbol or trading identifier of the targe. Set to asset currency for
   * 'sell' operation.
   */
  to_ticker?: string | null;
  /**
   * International Securities Identification Number of the target. Set to asset
   * currency for 'sell' operation.
   */
  to_isin?: string | null;
  /** Number of shares or units to trade */
  quantity: number;
  /** Price per share or unit in the base currency */
  price: number;
  /** Total amount for the operation in the base currency */
  amount: number;
};

/**
 * Proposal
 *
 * Proposal schema definition.
 */
export type Proposal = {
  /** Descriptive name for the trading proposal */
  name: string;
  /** Additional details or notes about the proposal */
  description?: string | null;
  /** Unique identifier for the proposal */
  id: number;
  /** Timestamp when the proposal was created */
  created_at: string;
  /** Information about the CITEC user who created the proposal */
  citec_user: User;
  /** List of trading operations included in the proposal */
  operations: ProposalOperation[];
};

/**
 * ProposalCreate
 *
 * Proposal create schema definition.
 */
export type ProposalCreate = {
  /** Descriptive name for the trading proposal */
  name: string;
  /** Additional details or notes about the proposal */
  description?: string | null;
  /** Base currency for the proposal */
  currency: string;
  /** List of trading operations to be executed */
  operations: ProposalOperation[];
  /** Current portfolio of the user */
  current_portfolio: Portfolio;
  /** Proposal portfolio for the user */
  proposal: Portfolio;
  /** Returns of the proposal */
  returns: TsSignal;
  /** Risk returns of the proposal */
  risk_returns: RiskReturn[];
};

/**
 * InvestmentProposalOperation
 *
 * Schema for an operation of an investment proposal.
 */
export type InvestmentProposalOperation = {
  /** Operation ID */
  id: number;
  /** Type of the operation */
  operation_type: 'buy' | 'sell' | 'transfer';
  /** Whether the operation was entered by shares or amount */
  input_type: 'shares' | 'amount';
  /** IBAN of the origin account */
  from_iban: string;
  /** Ticker of the origin asset */
  from_ticker?: string | null;
  /** ISIN of the origin asset */
  from_isin?: string | null;
  /** IBAN of the target account */
  to_iban: string;
  /** Ticker of the target asset */
  to_ticker?: string | null;
  /** ISIN of the target asset */
  to_isin?: string | null;
  /** Number of shares or units to trade */
  quantity: number;
  /** Price per share or unit */
  price: number;
  /** Total amount of the operation */
  amount: number;
  /** Last operation update timestamp */
  updated_at: string;
  /** Status of the related order, null when no order exists */
  order_status?:
    | 'pending'
    | 'sent_to_inversis'
    | 'executed'
    | 'rejected'
    | 'error'
    | null;
};

/**
 * InvestmentProposal
 *
 * Schema for an investment proposal.
 */
export type InvestmentProposal = {
  /** Investment proposal ID */
  id: number;
  /** Name of the investment proposal */
  name: string;
  /** Additional details about the investment proposal */
  description?: string | null;
  /** Status of the investment proposal */
  status: 'pending' | 'active' | 'inactive';
  /** ID of the advisor who created the investment proposal */
  user_id: number;
  /** Currency of the proposal */
  currency: string;
  /** Creation timestamp */
  created_at: string;
  /** Operations included in the investment proposal */
  operations?: InvestmentProposalOperation[];
};

/**
 * InvestmentProposals
 *
 * Schema for a list of investment proposals.
 */
export type InvestmentProposals = {
  /** List of investment proposals */
  proposals?: InvestmentProposal[];
};

/**
 * ProposalPositionsUpdate
 *
 * Proposal positions update schema definition.
 */
export type ProposalPositionsUpdate = {
  /** Unique identifier for the proposal position */
  id: number;
  /** Current status of the trading position */
  status: 'rejected' | 'accepted';
};

/**
 * ProposalPositionsUpdateList
 *
 * Proposal update list schema definition.
 */
export type ProposalPositionsUpdateList = {
  /** List of trading positions to be updated */
  positions: ProposalPositionsUpdate[];
};

/**
 * TargetPortfolioPosition
 *
 * Target portfolio position schema definition.
 */
export type TargetPortfolioPosition = {
  /** Stock symbol or trading identifier */
  ticker: string;
  /** International Securities Identification Number */
  isin?: string | null;
  /** Number of shares or units held */
  quantity: number;
  /** IBAN of the account holding the position */
  iban: string;
  /** Position value in the base currency */
  amount: number;
};

/**
 * TargetPortfolioOperation
 *
 * Schema for an operation to simulate over the current portfolio.
 */
export type TargetPortfolioOperation = {
  /**
   * Whether the operation is entered by shares or amount
   *
   * @default shares
   */
  input_type: 'shares' | 'amount';
  /** Type of the operation */
  operation_type: 'buy' | 'sell' | 'transfer';
  /** IBAN of the origin account */
  from_iban: string;
  /** Ticker of the origin asset */
  from_ticker?: string | null;
  /** ISIN of the origin asset */
  from_isin?: string | null;
  /** IBAN of the target account */
  to_iban: string;
  /** Ticker of the target asset */
  to_ticker?: string | null;
  /** ISIN of the target asset */
  to_isin?: string | null;
  /** Number of shares or units to trade */
  quantity: number;
  /** Price per unit of the target asset */
  to_price: number;
  /** Total amount of the operation */
  amount: number;
};

/**
 * TargetPortfolioRequest
 *
 * Schema to simulate a target portfolio.
 */
export type TargetPortfolioRequest = {
  /** Current positions held in the portfolio, cash included */
  current_portfolio: TargetPortfolioPosition[];
  /** List of operations to simulate over the current portfolio */
  operations: TargetPortfolioOperation[];
};

/**
 * OperationResult
 *
 * Schema for the outcome of a simulated operation.
 */
export type OperationResult = {
  /** Position of the operation in the request */
  index: number;
  /** Type of the operation */
  operation_type: 'buy' | 'sell' | 'transfer';
  /** Which field of the operation drove the simulation */
  input_type: 'shares' | 'amount';
  /** Whether the operation was fully, partially or not applied */
  status: 'applied' | 'partial' | 'rejected';
  /** Quantity asked for, derived from the amount in amount mode */
  requested_quantity: number;
  /** Amount asked for, derived from the quantity in shares mode */
  requested_amount: number;
  /** Quantity actually applied */
  applied_quantity: number;
  /** Amount actually applied */
  applied_amount: number;
  /** Why the operation could not be fully applied */
  reason_code?:
    | 'insufficient_quantity'
    | 'insufficient_cash'
    | 'missing_position'
    | 'missing_cash_position'
    | 'unvalued_position'
    | null;
};

/**
 * TargetPortfolioResponse
 *
 * Schema for the result of a target portfolio simulation.
 */
export type TargetPortfolioResponse = {
  /** Overall outcome of the simulation */
  status: 'success' | 'partial' | 'failed';
  /** Resulting positions after applying the operations */
  positions: TargetPortfolioPosition[];
  /** Outcome of each operation, in request order */
  operation_results: OperationResult[];
};

/**
 * TargetPortfolioCreate
 *
 * @deprecated Use {@link TargetPortfolioRequest}. Aggregator target-portfolio create
 *   schema (deprecated endpoint).
 */
export type TargetPortfolioCreate = {
  /** Current positions held in the portfolio. */
  current_portfolio: unknown[];
  /** List of operations to perform over the current portfolio. */
  operations: ProposalOperation[];
};

/**
 * PositionsReturnsRequest
 *
 * Positions returns request schema definition.
 */
export type PositionsReturnsRequest = {
  /**
   * Benchmark portfolio composition used to compare against the client's portfolio
   * returns
   */
  benchmark: unknown;
  /**
   * List of financial institution IDs to include in the portfolio returns
   * calculation
   */
  institution_ids: string[];
  /** Unique identifier of the client whose portfolio returns are being calculated */
  client_id: number;
  /** Initial date from which to calculate portfolio returns */
  start_date: string;
  /** Final date until which to calculate portfolio returns */
  end_date: string;
  /** Currency in which to express the returns. Supports USD and EUR */
  currency: string;
};

/**
 * BacktestReport
 *
 * Backtest report schema definition.
 */
export type BacktestReport = {
  /** Historical data used */
  historical_data: unknown;
  /** Risk report */
  risk: unknown;
  /** (Optional) Returns report */
  returns?: unknown | null;
  /** Backtest time series data for plotting */
  ts_backtest: unknown;
  /** Drawdown time series data for plotting */
  ts_drawdowns: unknown;
};

/**
 * AccountHoldingsInput
 *
 * Account holdings input schema definition.
 */
export type AccountHoldingsInput = {
  /** Unique identifier for the account. */
  account_id?: string | null;
  /** The name associated with the account. */
  account_name?: string | null;
  /** ID of the financial institution. */
  institution_id: string;
  /** Name of the financial institution. */
  institution_name: string;
  /** The currency in which the transaction was conducted. */
  currency?: string | null;
  /** List of account owners. */
  owners?: unknown[] | null;
  /** Error code. */
  error?: string | null;
  /** Datetime when the records were created */
  created_at?: string | null;
  /** Account balances. */
  balances?: unknown | null;
  /** Account holdings. */
  holdings: unknown[];
  /** List of debts associated with the account. */
  debts: unknown[];
};

/**
 * AccountHoldingsOutput
 *
 * Account holdings output schema definition.
 */
export type AccountHoldingsOutput = {
  /** Unique identifier for the account. */
  account_id?: string | null;
  /** The name associated with the account. */
  account_name?: string | null;
  /** ID of the financial institution. */
  institution_id: string;
  /** Name of the financial institution. */
  institution_name: string;
  /** The currency in which the transaction was conducted. */
  currency?: string | null;
  /** List of account owners. */
  owners?: unknown[] | null;
  /** Error code. */
  error?: string | null;
  /** Datetime when the records were created */
  created_at?: string | null;
  /** Account balances. */
  balances?: unknown | null;
  /** Account holdings. */
  holdings: unknown[];
  /** List of debts associated with the account. */
  debts: unknown[];
};

/**
 * AccountTransactionsInput
 *
 * Account transactions input schema definition.
 */
export type AccountTransactionsInput = {
  /** Unique identifier for the account. */
  account_id?: string | null;
  /** The name associated with the account. */
  account_name?: string | null;
  /** ID of the financial institution. */
  institution_id: string;
  /** Name of the financial institution. */
  institution_name: string;
  /** The currency in which the transaction was conducted. */
  currency?: string | null;
  /** List of account owners. */
  owners?: unknown[] | null;
  /** Error code. */
  error?: string | null;
  /** Datetime when the records were created */
  created_at?: string | null;
  /** List of transactions for the account. */
  transactions: unknown[];
};

/**
 * AccountTransactionsOutput
 *
 * Account transactions output schema definition.
 */
export type AccountTransactionsOutput = {
  /** Unique identifier for the account. */
  account_id?: string | null;
  /** The name associated with the account. */
  account_name?: string | null;
  /** ID of the financial institution. */
  institution_id: string;
  /** Name of the financial institution. */
  institution_name: string;
  /** The currency in which the transaction was conducted. */
  currency?: string | null;
  /** List of account owners. */
  owners?: unknown[] | null;
  /** Error code. */
  error?: string | null;
  /** Datetime when the records were created */
  created_at?: string | null;
  /** List of transactions for the account. */
  transactions: unknown[];
};

//#endregion AGGREGATOR SCHEMA TYPES

//#region AGGREGATOR ENDPOINT TYPES

/**
 * Path and query parameters for Link Create Create a link token to be used in the
 * Plaid Link flow.
 */
export type GetAggregatorLinkCreateParams = {
  /** ID of the financial institution */
  institution_id?: string | null;
};

/** Response body for Link Create */
export type GetAggregatorLinkCreateResponseBody = LinkTokenResponse;

/** Path and query parameters for Create Proposal Create a proposal for a client. */
export type CreateAggregatorProposalsParams = ClientIdParam;

/** Request body for Create Proposal */
export type CreateAggregatorProposalsRequestBody = ProposalCreate;

/**
 * Response body for Create Proposal (`POST /v2/advisory/investment-proposals/`,
 * 201)
 */
export type CreateAggregatorProposalsResponseBody = unknown;

/** Path and query parameters for Get Investment Proposals */
export type GetInvestmentProposalsParams = ClientIdParam;

/** Response body for Get Investment Proposals */
export type GetInvestmentProposalsResponseBody = InvestmentProposals;

/**
 * Path and query parameters for Delete Investment Proposal `DELETE
 * /v2/advisory/investment-proposals/{proposal_id}/`
 */
export type DeleteInvestmentProposalParams = {
  /** ID of the investment proposal */
  proposal_id: number;
} & ClientIdParam;

/** Response body for Delete Investment Proposal */
export type DeleteInvestmentProposalResponseBody = void;

/** Request body for Create Target Portfolio */
export type CreateAggregatorProposalsTargetPortfolioRequestBody =
  TargetPortfolioRequest;

/** Response body for Create Target Portfolio */
export type CreateAggregatorProposalsTargetPortfolioResponseBody =
  TargetPortfolioResponse;

/** Response body for List Advisors */
export type GetAggregatorUsersResponseBody = Advisor[];

/**
 * Path and query parameters for List Institutions Get all available linked
 * institutions.
 */
export type GetAggregatorInstitutionsParams = {
  /** Include additional information. */
  details?: boolean;
} & ClientIdParam;

/** Response body for List Institutions */
export type GetAggregatorInstitutionsResponseBody = Institution[];

/** Request body for Create Institution */
export type CreateAggregatorInstitutionsRequestBody = InstitutionCredentials;

/** Response body for Create Institution */
export type CreateAggregatorInstitutionsResponseBody = Institution;

/**
 * Path and query parameters for Institutions All Cashflow Get cashflow for all
 * linked institutions.
 */
export type GetAggregatorInstitutionsCashflowParams = DateRangeParams &
  ClientIdParam;

/** Response body for Institutions All Cashflow */
export type GetAggregatorInstitutionsCashflowResponseBody = Cashflow;

/** Request body for Positions Returns Get portfolio returns for a client. */
export type CreateAggregatorInstitutionsPositionsReturnsRequestBody =
  PositionsReturnsRequest;

/** Response body for Positions Returns */
export type CreateAggregatorInstitutionsPositionsReturnsResponseBody =
  BacktestReport;

/**
 * Path and query parameters for Institution Deletes an institution by its
 * institution ID.
 */
export type DeleteAggregatorInstitutionsInstitutionIdParams = InstitutionIdParam;

/** Response body for Institution */
export type DeleteAggregatorInstitutionsInstitutionIdResponseBody = void;

/**
 * Path and query parameters for Institution Cashflow Get cashflow for a linked
 * institution.
 */
export type GetAggregatorInstitutionsInstitutionIdCashflowParams =
  InstitutionIdParam & DateRangeParams & ClientIdParam;

/** Response body for Institution Cashflow */
export type GetAggregatorInstitutionsInstitutionIdCashflowResponseBody = Cashflow;

/** Path and query parameters for Institution Positions Delete Institution positions. */
export type DeleteAggregatorInstitutionsInstitutionIdPositionsParams =
  InstitutionIdParam &
    ClientIdParam & {
      /** IDs of the positions to delete */
      ids: string;
    };

/** Response body for Institution Positions Delete */
export type DeleteAggregatorInstitutionsInstitutionIdPositionsResponseBody =
  unknown;

/**
 * Path and query parameters for Institution Positions Add or update Institution
 * positions.
 */
export type CreateAggregatorInstitutionsInstitutionIdPositionsParams =
  InstitutionIdParam & ClientIdParam;

/** Request body for Institution Positions */
export type CreateAggregatorInstitutionsInstitutionIdPositionsRequestBody =
  AccountHoldingsInput[];

/** Response body for Institution Positions */
export type CreateAggregatorInstitutionsInstitutionIdPositionsResponseBody =
  AccountHoldingsOutput[];

/**
 * Path and query parameters for Delete Manual Transactions Delete transactions by
 * their IDs provided as a comma-separated str.
 */
export type DeleteAggregatorInstitutionsInstitutionIdTransactionsParams =
  InstitutionIdParam &
    ClientIdParam & {
      /** Comma-separated string of transaction IDs to delete. */
      transaction_ids: string;
    };

/** Response body for Delete Manual Transactions */
export type DeleteAggregatorInstitutionsInstitutionIdTransactionsResponseBody =
  void;

/**
 * Path and query parameters for Institution Transactions Get transactions for a
 * linked institution.
 */
export type GetAggregatorInstitutionsInstitutionIdTransactionsParams =
  InstitutionIdParam & DateRangeParams & RefreshParam & ClientIdParam;

/** Response body for Institution Transactions */
export type GetAggregatorInstitutionsInstitutionIdTransactionsResponseBody =
  InProgressResponse;

/**
 * Path and query parameters for Add Manual Transactions Add a list of transactions
 * manually for a linked institution.
 */
export type CreateAggregatorInstitutionsInstitutionIdTransactionsParams =
  InstitutionIdParam & ClientIdParam;

/** Request body for Add Manual Transactions */
export type CreateAggregatorInstitutionsInstitutionIdTransactionsRequestBody =
  AccountTransactionsInput[];

/** Response body for Add Manual Transactions */
export type CreateAggregatorInstitutionsInstitutionIdTransactionsResponseBody =
  AccountTransactionsOutput[];

//#endregion AGGREGATOR ENDPOINT TYPES

//#region ASSETS SCHEMA TYPES

/**
 * Asset
 *
 * Asset schema definition.
 */
export type Asset = {
  /** Asset ticker */
  ticker: string;
  /** Market identifier */
  market: string;
  /** Asset name */
  name: string;
};

/**
 * AssetValidation
 *
 * Asset validation schema definition.
 */
export type AssetValidation = {
  /** Asset ticker */
  ticker: string;
  /** Asset availability */
  availability: string;
};

/**
 * CoverageValidationRequest
 *
 * Coverage validation request schema definition.
 */
export type CoverageValidationRequest = {
  /** List of ticker symbols to validate */
  tickers: string[];
  /** Account identification */
  account_id?: BrokerageInfo | null;
};

//#endregion ASSETS SCHEMA TYPES

//#region ASSETS ENDPOINT TYPES

/**
 * Path and query parameters for Coverage Validation Validate if a broker supports
 * trades for a list of tickers.
 */
export type CreateAssetsCoverageValidationParams = BrokerConfig;

/** Request body for Coverage Validation */
export type CreateAssetsCoverageValidationRequestBody = CoverageValidationRequest;

/** Response body for Coverage Validation */
export type CreateAssetsCoverageValidationResponseBody = AssetValidation[];

/** Path and query parameters for Stocks Get asset details. */
export type CreateAssetsStocksParams = BrokerConfig & {
  /** Ticker symbol (optional) */
  ticker?: string | null;
};

/** Request body for Stocks */
export type CreateAssetsStocksRequestBody = {
  /** List of ticker symbols */
  stocks: string[];
};

/** Response body for Stocks */
export type CreateAssetsStocksResponseBody = Asset[];

//#endregion ASSETS ENDPOINT TYPES

//#region CURRENCY ENDPOINT TYPES

/** Request body for Prices */
export type CreateCurrencyPricesRequestBody = {
  /** List of currency pairs */
  currencies: string[];
};

/** Response body for Prices */
export type CreateCurrencyPricesResponseBody = Record<string, number>[];

//#endregion CURRENCY ENDPOINT TYPES

//#region EQUITY SCHEMA TYPES

/**
 * Price
 *
 * Price schema definition.
 */
export type Price = {
  /** Currency */
  currency?: string | null;
  /** Price */
  price?: number | null;
  /** Ticker */
  ticker: string;
  /** Status */
  status: string;
  /** Local currency values */
  local_currency_values?: unknown | null;
};

//#endregion EQUITY SCHEMA TYPES

//#region EQUITY ENDPOINT TYPES

/**
 * Path and query parameters for Citec Prices Get prices in Finnhub API for a list
 * of tickers.
 */
export type CreateEquityCitecPriceParams = CurrencyParam;

/** Request body for Citec Prices */
export type CreateEquityCitecPriceRequestBody = {
  /** List of tickers */
  tickers: string[];
};

/** Response body for Citec Prices */
export type CreateEquityCitecPriceResponseBody = Price[];

/** Path and query parameters for Prices Get broker prices for a list of tickers. */
export type CreateEquityPricesParams = BrokerConfigWithCurrencyParam;

/** Request body for Prices */
export type CreateEquityPricesRequestBody = {
  /** List of tickers */
  tickers: string[];
};

/** Response body for Prices */
export type CreateEquityPricesResponseBody = Price[];

//#endregion EQUITY ENDPOINT TYPES

//#region NOTIFICATION SCHEMA TYPES

/**
 * AdvisoryProposalPayload
 *
 * Advisory proposal payload schema definition.
 */
export type AdvisoryProposalPayload = {
  /** Type of advisory proposal */
  type: 'advisory_proposal';
  /** Client ID */
  client_id: number;
};

//#endregion NOTIFICATION SCHEMA TYPES

//#region NOTIFICATION ENDPOINT TYPES

/** Request body for Post Notification */
export type CreateNotificationRequestBody = AdvisoryProposalPayload;

/** Response body for Post Notification */
export type CreateNotificationResponseBody = void;

//#endregion NOTIFICATION ENDPOINT TYPES

//#region PORTFOLIO SCHEMA TYPES

/**
 * Order
 *
 * Order schema definition.
 */
export type Order = {
  /** Order ID */
  order_id?: string | null;
  /** Ticker */
  ticker: string;
  /** Number of stocks */
  stocks_number: number;
  /** Order side */
  side: string;
  /** Order status */
  status: string;
  /** Account ID */
  account_id?: string | null;
};

/**
 * OrderFilter
 *
 * Order filter schema definition.
 */
export type OrderFilter = {
  /** Order side */
  side?: string | null;
  /** Order status */
  status?: string | null;
  /** Order start date (MM/DD/YYYY) */
  start_date?: string | null;
  /** Order end date (MM/DD/YYYY) */
  end_date?: string | null;
};

/**
 * PortfolioPerformance
 *
 * Portfolio performance schema definition.
 */
export type PortfolioPerformance = {
  /** Date */
  datetime: string[];
  /** Investment amount */
  outstanding_balance: number[];
  /** Capital gains */
  capital_gains: number;
  /** Currency */
  currency: string;
  /** Performance */
  performance: unknown[];
  /** Monthly Performance */
  monthly_performance: unknown[];
};

/**
 * PortfolioPosition
 *
 * Portfolio position schema definition.
 */
export type PortfolioPosition = {
  /** Profit and Loss of the position in USD */
  unrealized_capital_gains: number;
  /** Market value of the position in USD */
  outstanding_balance: number;
  /** Currency for the position values */
  currency: AccountCurrency | null;
  /** Symbol or ticker of the position */
  ticker: string;
  /** Market exchange */
  exchange: string;
  /** Position asset type */
  asset_type: string;
  /** Number of contracts for the position */
  stocks_number: number;
  /** Values in local currency */
  local_currency_values?: unknown | null;
};

//#endregion PORTFOLIO SCHEMA TYPES

//#region PORTFOLIO ENDPOINT TYPES

/**
 * Path and query parameters for Orders Deletes the indicated active orders already
 * placed in user's broker account.
 */
export type DeletePortfolioOrdersParams = BrokerConfig;

/** Request body for Orders */
export type DeletePortfolioOrdersRequestBody = {
  /** List with Orders */
  orders: Order[];
  /** Account identification */
  account_id: string;
};

/** Response body for Orders */
export type DeletePortfolioOrdersResponseBody = Order[];

/**
 * Path and query parameters for Orders Updates the info (quantity, side) of active
 * orders in user's broker account.
 */
export type UpdatePortfolioOrdersParams = BrokerConfig;

/** Request body for Orders */
export type UpdatePortfolioOrdersRequestBody = {
  /** List with Orders */
  orders: Order[];
  /** Account identification */
  account_id: string;
};

/** Response body for Orders */
export type UpdatePortfolioOrdersResponseBody = Order[];

/** Path and query parameters for Orders Places new orders to trade in user's broker. */
export type CreatePortfolioOrdersParams = BrokerConfig;

/** Request body for Orders */
export type CreatePortfolioOrdersRequestBody = {
  /** List with Orders */
  orders: Order[];
  /** Account identification */
  account_id: string;
};

/** Response body for Orders */
export type CreatePortfolioOrdersResponseBody = Order[];

/**
 * Path and query parameters for Orders Search Returns a list with filtered orders
 * info from the user's broker account.
 */
export type CreatePortfolioOrdersSearchParams = BrokerConfig;

/** Request body for Orders Search */
export type CreatePortfolioOrdersSearchRequestBody = {
  /** Account identification (optional) */
  account_id?: string | null;
  /** OrderFilter object with filter data */
  order_filter?: OrderFilter | null;
};

/** Response body for Orders Search */
export type CreatePortfolioOrdersSearchResponseBody = Order[];

/** Path and query parameters for Performance Get Portfolio performance. */
export type CreatePortfolioPerformanceParams = BrokerConfigWithCurrencyParam & {
  /** Period you want to retrieve historical portfolio data */
  period: string;
};

/** Request body for Performance */
export type CreatePortfolioPerformanceRequestBody = BrokerageInfo;

/** Response body for Performance */
export type CreatePortfolioPerformanceResponseBody = PortfolioPerformance;

/** Path and query parameters for Positions Get positions from the broker. */
export type CreatePortfolioPositionsParams = BrokerConfigWithCurrencyParam;

/** Request body for Positions */
export type CreatePortfolioPositionsRequestBody = BrokerageInfo;

/** Response body for Positions */
export type CreatePortfolioPositionsResponseBody = PortfolioPosition[];

//#endregion PORTFOLIO ENDPOINT TYPES

//#region SETTINGS ENDPOINT TYPES

/** Response body for Root */
export type GetRawResponseBody = unknown;

/** Response body for Auth */
export type GetSettingsAuthResponseBody = unknown;

/** Response body for Ping */
export type GetSettingsPingResponseBody = unknown;

/** Response body for Ping Private */
export type GetSettingsPingPrivateResponseBody = unknown;

//#endregion SETTINGS ENDPOINT TYPES

//#region WEBHOOKS ENDPOINT TYPES

/** Response body for Lleida.net Signature Webhook */
export type CreateWebhooksLleidanetSignatureResponseBody = void;

//#endregion WEBHOOKS ENDPOINT TYPES
